import { useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { useFetchWithAuth } from '../../session/useFetchWithAuth'

const API_URL = import.meta.env.VITE_EPC_API_URL ?? ''

interface CreateSchoolInput {
  name: string
  district: string
  city: string
}

interface UpdateSchoolInput {
  name: string
  district: string
  city: string
}

export const useCreateSchoolMutation = () => {
  const fetchWithAuth = useFetchWithAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: CreateSchoolInput) => {
      const response = await fetchWithAuth(`${API_URL}/admin/schools`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      })
      return response
    },
    onError: (error: any) => {
      console.error('Create school error:', error)
      // Check for duplicate error (409 status)
      if (error.message?.includes('already exists') || error.status === 409) {
        toast.error(error.message || 'This school already exists')
      } else {
        toast.error(error.message || 'Failed to create school')
      }
    },
    onSuccess: () => {
      toast.success('School created successfully')
      queryClient.invalidateQueries({ queryKey: ['adminSchools'] })
      queryClient.invalidateQueries({ queryKey: ['adminDistricts'] })
      queryClient.invalidateQueries({ queryKey: ['adminCities'] })
    },
  })
}

export const useUpdateSchoolMutation = () => {
  const fetchWithAuth = useFetchWithAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string
      data: UpdateSchoolInput
    }) => {
      const response = await fetchWithAuth(`${API_URL}/admin/schools/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      })
      return response
    },
    onError: (error: any) => {
      console.error('Update school error:', error)
      if (error.message?.includes('already exists') || error.status === 409) {
        toast.error(error.message || 'This school already exists')
      } else {
        toast.error(error.message || 'Failed to update school')
      }
    },
    onSuccess: (data, variables) => {
      toast.success('School updated successfully')
      queryClient.invalidateQueries({ queryKey: ['adminSchools'] })
      queryClient.invalidateQueries({ queryKey: ['adminSchool', variables.id] })
      queryClient.invalidateQueries({ queryKey: ['adminDistricts'] })
      queryClient.invalidateQueries({ queryKey: ['adminCities'] })
    },
  })
}

export const useDeleteSchoolMutation = () => {
  const fetchWithAuth = useFetchWithAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      await fetchWithAuth(`${API_URL}/admin/schools/${id}`, {
        method: 'DELETE',
      })
    },
    onError: (error: any) => {
      console.error('Delete school error:', error)
      toast.error(error.message || 'Failed to delete school')
    },
    onSuccess: () => {
      toast.success('School deleted successfully')
      queryClient.invalidateQueries({ queryKey: ['adminSchools'] })
      queryClient.invalidateQueries({ queryKey: ['adminDistricts'] })
      queryClient.invalidateQueries({ queryKey: ['adminCities'] })
    },
  })
}
